import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CustomDropdownWithFetch = ({
  label,
  onSelect,
  placeholder = "Select...",
  colorLabel,
  font,
  search,
  preselectedValue,
  parentClass,
    disabled,
  fetchOptions, // function to fetch options from API (page, searchTerm)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    preselectedValue ? preselectedValue.label : null
  );

  const dropdownRef = useRef();
  const listRef = useRef();

  useEffect(() => {
    if (preselectedValue) {
      setSelectedOption(preselectedValue.label);
    } else {
      setOptions([]);
    }
  }, [preselectedValue]);

  // Fetch data
  useEffect(() => {
    if (!isOpen) return;

    const loadOptions = async () => {
      setLoading(true);
      setOptions([]);
      try {
        const data = await fetchOptions(page, searchTerm);
        if (page === 1) {
          // Fade out old list first for smoother UX
          setOptions(data.items);
        } else {
          setOptions((prev) => [...prev, ...data.items]);
        }
        setHasMore(data.hasMore);
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, [isOpen, page, searchTerm]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Infinite scroll
  const handleScroll = () => {
    if (!listRef.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSelect = (option) => {
    setSelectedOption(option.label);
    setIsOpen(false);
    onSelect(option);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label
          className={`block w-full mb-2 text-sm ${
            colorLabel ? colorLabel : "text-[#464646]"
          }`}
        >
          {label}
        </label>
      )}

      {/* Button */}
      <button
      disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
          setPage(1);
        }}
        className={`${parentClass?parentClass:"border-gray-300"} relative w-full border font-medium  min-h-10 rounded-md text-sm px-2 pr-10 bg-white flex items-center text-left focus:outline-none`}
      >
        <span
          className={`${
            font === "small" ? "text-[12px]" : "whitespace-normal pr-2"
          } break-words`}
        >
          {selectedOption || placeholder}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-black pointer-events-none"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06-.02L10 10.69l3.71-3.5a.75.75 0 111.04 1.08l-4 3.75a.75.75 0 01-1.04 0l-4-3.75a.75.75 0 01-.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 mt-2 w-full bg-white border rounded-md shadow-lg z-50"
            style={{ maxHeight: "250px" }}
          >
            {/* Search */}
            {search && (
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border-b focus:outline-none"
              />
            )}

            {/* Options list */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="overflow-y-auto"
              style={{ maxHeight: "200px" }}
            >
              <AnimatePresence>
                {options.length > 0 ? (
                  options.map((option) => (
                    <motion.div
                      key={option.value}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => handleSelect(option)}
                      className="px-4 py-2 hover:bg-black hover:text-white cursor-pointer"
                    >
                      {option.label}
                    </motion.div>
                  ))
                ) : !loading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-4 py-2 text-gray-500"
                  >
                    No results found
                  </motion.div>
                ) : null}
              </AnimatePresence>

              {loading && (
                <div className="px-4 py-2 text-sm text-gray-500 animate-pulse">
                  Loading...
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdownWithFetch;
